import React from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { getBatchStatusSeverity } from '../../../constants';

/**
 * BatchHistory Component
 *
 * Displays recent batch processing history
 *
 * @component
 */
const BatchHistory = ({ batches = [], loading = false, onViewResults, onDownload, onRefresh, onReprocess, downloadingBatchIds = [], reprocessingBatchIds = [], selectedTeam = '' }) => {
  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Status column template
   */
  const statusBodyTemplate = (rowData) => {
    const isStuck = rowData.isStuck === true;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Tag
          value={isStuck ? 'Stuck' : rowData.status}
          severity={isStuck ? 'danger' : getBatchStatusSeverity(rowData.status)}
        />
        {isStuck && (
          <i className="pi pi-exclamation-triangle" style={{ color: '#e53e3e', fontSize: '0.9rem' }}
             title="This batch has been stuck in processing for over 60 minutes" />
        )}
      </div>
    );
  };

  /**
   * Team column template (no color/severity)
   */
  const teamBodyTemplate = (rowData) => {
    return <span>{rowData.team}</span>;
  };

  /**
   * Records column template
   */
  const recordsBodyTemplate = (rowData) => (
    <span>
      {rowData.processedCount || 0} / {rowData.rowCount || 0}
    </span>
  );

  /**
   * Failed count column template
   */
  const failedBodyTemplate = (rowData) => (
    <span>{rowData.failedCount || 0}</span>
  );

  /**
   * Date column template
   */
  const dateBodyTemplate = (rowData) => (
    <span>{formatDate(rowData.uploadedAt)}</span>
  );

  /**
   * Actions column template
   */
  const actionsBodyTemplate = (rowData) => {
    const isCompleted = rowData.status === 'COMPLETED';
    const isFailed = rowData.status === 'FAILED';
    const isStuck = rowData.isStuck === true;
    const canReprocess = rowData.canReprocess === true || isCompleted || isFailed;
    const canViewResults = isCompleted || isFailed || isStuck;
    const isDownloading = downloadingBatchIds.includes(rowData.batchId);
    const isReprocessing = reprocessingBatchIds.includes(rowData.batchId);
    const hasFailed = (rowData.failedCount || 0) > 0;

    return (
      <div className="actions-buttons">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-info"
          onClick={() => onViewResults(rowData.batchId)}
          tooltip="View Results"
          tooltipOptions={{ position: 'top' }}
          disabled={!canViewResults}
        />
        <Button
          icon={isDownloading ? undefined : 'pi pi-download'}
          className="p-button-rounded p-button-text p-button-success"
          onClick={() => onDownload(rowData.batchId)}
          tooltip="Download Results"
          tooltipOptions={{ position: 'top' }}
          disabled={!canViewResults || isDownloading}
          loading={isDownloading}
        />
        {canReprocess && hasFailed && (
          <Button
            icon={isReprocessing ? undefined : 'pi pi-replay'}
            className="p-button-rounded p-button-text p-button-warning"
            onClick={() => onReprocess(rowData.batchId, 'FAILED_ONLY')}
            tooltip="Re-process Failed Rows Only"
            tooltipOptions={{ position: 'top' }}
            disabled={isReprocessing}
            loading={isReprocessing}
          />
        )}
        {canReprocess && (
          <Button
            icon={isReprocessing ? undefined : 'pi pi-refresh'}
            className={`p-button-rounded p-button-text ${isStuck ? 'p-button-danger' : 'p-button-secondary'}`}
            onClick={() => onReprocess(rowData.batchId, 'ALL')}
            tooltip={isStuck ? 'Batch is stuck — Re-process All Rows' : 'Re-process All Rows'}
            tooltipOptions={{ position: 'top' }}
            disabled={isReprocessing}
            loading={isReprocessing}
          />
        )}
      </div>
    );
  };

  return (
    <div className="batch-history-container">
      <div className="history-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h3>Recent Batches</h3>
          <p className="subtitle">View your recent batch processing history</p>
        </div>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          className="p-button-outlined p-button-secondary"
          onClick={onRefresh}
          loading={loading}
          tooltip="Refresh batch status"
          tooltipOptions={{ position: 'left' }}
        />
      </div>

      <DataTable
        value={batches || []}
        loading={loading}
        emptyMessage={selectedTeam ? 'No batch history found. Upload a file to get started.' : 'Select a team above to see batch history.'}
        className="batch-history-table"
        stripedRows
        responsiveLayout="scroll"
      >
        <Column
          field="batchId"
          header="Batch ID"
          style={{ minWidth: '180px' }}
        />
        <Column
          field="fileName"
          header="File Name"
          style={{ minWidth: '200px' }}
        />
        <Column
          field="team"
          header="Team"
          body={teamBodyTemplate}
          style={{ minWidth: '150px' }}
        />
        <Column
          header="Records"
          body={recordsBodyTemplate}
          style={{ minWidth: '120px' }}
        />
        <Column
          field="status"
          header="Status"
          body={statusBodyTemplate}
          style={{ minWidth: '120px' }}
        />
        <Column
          header="Uncategorized"
          body={failedBodyTemplate}
          style={{ minWidth: '80px' }}
        />
        <Column
          field="uploadedAt"
          header="Created"
          body={dateBodyTemplate}
          style={{ minWidth: '180px' }}
        />
        <Column
          body={actionsBodyTemplate}
          header="Actions"
          exportable={false}
          style={{ minWidth: '200px', textAlign: 'center' }}
        />
      </DataTable>
    </div>
  );
};

BatchHistory.propTypes = {
  batches: PropTypes.arrayOf(PropTypes.shape({
    batchId: PropTypes.string,
    fileName: PropTypes.string,
    team: PropTypes.string,
    status: PropTypes.string,
    processedCount: PropTypes.number,
    rowCount: PropTypes.number,
    failedCount: PropTypes.number,
    uploadedAt: PropTypes.string
  })),
  loading: PropTypes.bool,
  onViewResults: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onReprocess: PropTypes.func.isRequired,
  downloadingBatchIds: PropTypes.arrayOf(PropTypes.string),
  reprocessingBatchIds: PropTypes.arrayOf(PropTypes.string),
  selectedTeam: PropTypes.string
};

export default BatchHistory;

